import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Mail, Clock, Save } from "lucide-react";
import api from "@/lib/api";
import { Button, Card, Field, Input, Select } from "@/components/ui-kit";

/**
 * Email Report Settings Panel — used inside Settings page as a tab.
 * Stores gmail_address, app_password (encrypted server-side via Fernet),
 * recipients list, report_time (HH:MM WIB), low_stock_only toggle.
 * "Kirim Test" button immediately sends a test email.
 */
export function EmailReportSettings() {
  const [cfg, setCfg] = useState({
    enabled: false,
    gmail_address: "",
    app_password: "",
    recipients: "",       // comma-separated UI; split on save
    report_time: "08:00",
    include_low_stock_only: false,
    has_password: false,
    last_sent_at: "",
    last_error: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/email-report-settings");
      setCfg((c) => ({
        ...c,
        ...r.data,
        recipients: (r.data.recipients || []).join(", "),
        app_password: "",  // never echo
      }));
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const recipients = (cfg.recipients || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (!cfg.gmail_address || !cfg.gmail_address.includes("@")) {
      toast.error("Alamat Gmail tidak valid");
      return;
    }
    if (recipients.length === 0) {
      toast.error("Minimal 1 alamat penerima");
      return;
    }
    if (!cfg.has_password && !cfg.app_password) {
      toast.error("Gmail App Password wajib pada konfigurasi pertama");
      return;
    }
    setSaving(true);
    try {
      await api.post("/email-report-settings", {
        enabled: !!cfg.enabled,
        gmail_address: cfg.gmail_address.trim(),
        app_password: cfg.app_password || undefined,
        recipients,
        report_time: cfg.report_time,
        include_low_stock_only: !!cfg.include_low_stock_only,
      });
      toast.success("Pengaturan email tersimpan ✓");
      setCfg((c) => ({ ...c, app_password: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/email-report-settings/test");
      toast.success(r.data.message || "Email test terkirim");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim test");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="p-5 space-y-5" data-testid="settings-email-report">
      <div>
        <h3 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2">
          <Mail className="h-4 w-4 text-terra-600" /> Update Stok Email Harian
        </h3>
        <p className="text-xs text-stone-500 mt-1 max-w-2xl">
          Kirim ringkasan stok material ke email setiap hari pada jam yang ditentukan via Gmail SMTP (gratis).
          Gunakan <b>Gmail App Password</b> (bukan password utama akun) — buat di{" "}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-terra-700 underline">myaccount.google.com/apppasswords</a>
          {" "}(perlu 2-Step Verification aktif).
        </p>
      </div>

      <label className="flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={!!cfg.enabled}
          onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          data-testid="email-enabled-toggle"
        />
        <span className="font-medium">Aktifkan pengiriman harian otomatis</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Gmail Pengirim" required>
          <Input
            type="email"
            value={cfg.gmail_address}
            onChange={(e) => setCfg({ ...cfg, gmail_address: e.target.value })}
            placeholder="nama@gmail.com"
            data-testid="email-gmail-address"
          />
        </Field>
        <Field label={`Gmail App Password ${cfg.has_password ? "(tersimpan)" : "(wajib pada setup pertama)"}`}>
          <Input
            type="password"
            value={cfg.app_password}
            onChange={(e) => setCfg({ ...cfg, app_password: e.target.value })}
            placeholder={cfg.has_password ? "Biarkan kosong untuk pakai yang lama" : "16 karakter dari Google"}
            data-testid="email-app-password"
            autoComplete="new-password"
          />
        </Field>
        <Field label="Penerima Email" required hint="Pisahkan dengan koma untuk multiple penerima. Contoh: gudang@perusahaan.com, supervisor@perusahaan.com, owner@perusahaan.com">
          <Input
            value={cfg.recipients}
            onChange={(e) => setCfg({ ...cfg, recipients: e.target.value })}
            placeholder="gudang@perusahaan.com, supervisor@perusahaan.com"
            data-testid="email-recipients"
          />
        </Field>
        <Field label="Jam Kirim (WIB)" required>
          <div className="relative">
            <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
            <Input
              type="time"
              value={cfg.report_time}
              onChange={(e) => setCfg({ ...cfg, report_time: e.target.value })}
              className="!pl-7"
              data-testid="email-report-time"
            />
          </div>
        </Field>
        <div className="md:col-span-2">
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input
              type="checkbox"
              checked={!!cfg.include_low_stock_only}
              onChange={(e) => setCfg({ ...cfg, include_low_stock_only: e.target.checked })}
              data-testid="email-low-stock-only"
            />
            Hanya kirim item dengan stok rendah (di bawah batas minimum)
          </label>
        </div>
      </div>

      {cfg.last_sent_at && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 font-mono">
          ✓ Terakhir terkirim: {cfg.last_sent_at?.slice(0, 16).replace("T", " ")} WIB
        </div>
      )}
      {cfg.last_error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2">
          <b>Error terakhir:</b> {cfg.last_error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
        <Button
          variant="ghost"
          onClick={sendTest}
          disabled={testing || !cfg.has_password}
          title={!cfg.has_password ? "Simpan dulu pengaturan dengan App Password" : "Kirim email test sekarang"}
          data-testid="email-send-test"
        >
          <Send className="h-3.5 w-3.5" /> {testing ? "Mengirim…" : "Kirim Test"}
        </Button>
        <Button onClick={save} disabled={saving} data-testid="email-save-btn">
          <Save className="h-3.5 w-3.5" /> {saving ? "Menyimpan…" : "Simpan"}
        </Button>
      </div>
    </Card>
  );
}
